/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'primereact/tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPen,
  faTimes,
  faCheck,
  faCheckCircle,
  faWarning,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { InputNumberField, LoadingSpinner } from '@/components';
import { InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { useToastNotification } from '@/hooks';
import { userHasPermission } from '@/utils/auth';
import useAuthContext from '@/hooks/useAuthContext';

import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import styles from './EditVGM.module.scss';

import { postSolasVGM } from './postSolasVGM';
import { SolasInfo } from '../../types';

export type ParsedSolasInfo = Omit<
  SolasInfo,
  'tipoContenedor' & 'cierreStacking'
> & {
  tipo?: string;
  tamano?: string;
};

interface EditVGMProps {
  currentVGMnumber: string;
  oidExport: number;
  cierreStacking: string;
}

const EditVGM: React.FC<EditVGMProps> = ({
  currentVGMnumber,
  oidExport,
  cierreStacking,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [vgmValue, setVgmValue] = useState<number | null>(null);
  const { showToast } = useToastNotification();
  const [isSuccessColumn, setIsSuccessColumn] = useState(false);
  const { t } = useTranslation('solas');
  const lastUpdatedOidRef = useRef<number | null>(null);
  const queryClient = useQueryClient();
  const { userDataDecoded } = useAuthContext();

  const { mutate: saveVgmValue, isPending: saving } = useMutation({
    mutationFn: postSolasVGM,
    onSuccess: () => {
      setEditMode(false);
      setIsSuccessColumn(true);
      queryClient.invalidateQueries({ queryKey: ['solas'] });
    },
    onError: (error) => {
      if (error.toString().trim() !== 'Error') {
        showToast({ severity: 'error', detail: `${error}` });
      } else {
        showToast({
          severity: 'error',
          detail: t('unexpectedError'),
        });
      }
    },
  });

  const [vgmFieldError, setVgmFieldError] = useState<string>('');

  const handleChange = (e: InputNumberValueChangeEvent) => {
    setVgmValue(e.value as number | null);
    setVgmFieldError('');
  };

  useEffect(() => {
    setVgmValue(Number(currentVGMnumber));
  }, [currentVGMnumber]);

  const handleSaveClick = () => {
    if (!vgmValue || vgmValue.toString().length > 5) {
      setVgmFieldError(t('vgmPlaceholder'));
      return;
    }
    setVgmFieldError('');
    saveVgmValue({
      items: [{ oidExport, peso: vgmValue }],
      listadoAgencia: userDataDecoded?.ftpVERMAS || '',
    });
  };

  const handleCancelClick = () => {
    setEditMode(false);
    setVgmValue(Number(currentVGMnumber));
    setVgmFieldError('');
  };

  useEffect(() => {
    if (lastUpdatedOidRef.current === oidExport) {
      setIsSuccessColumn(true);
    }
    if (lastUpdatedOidRef.current !== oidExport) {
      setIsSuccessColumn(false);
    }
  }, [oidExport]);

  const cierrStackingDate = new Date(cierreStacking);

  const today = new Date();

  cierrStackingDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const hasPermission = userHasPermission('Enviar_VGM_SOLAS');

  return (
    <div
      className={classNames(
        styles.editVgmContainer,
        editMode && styles.editMode
      )}
    >
      <InputNumberField
        id="vgm-declared"
        name="vgmValue"
        value={currentVGMnumber === '0' ? null : vgmValue}
        placeholder={currentVGMnumber === '0' ? t('vgmPlaceholder') : ''}
        onChange={handleChange}
        error={vgmFieldError}
        allowOnlyWholeNumbers
        minNumber={1}
        classnames={styles.inputField}
        thousandSeparators
        onFocus={() => {
          setEditMode(true);
        }}
        maxLength={6}
        iconError={faWarning}
        disabled={
          cierrStackingDate.getTime() < today.getTime() || !hasPermission
        }
      />
      {editMode && !saving && (
        <>
          <FontAwesomeIcon
            icon={faCheck}
            onClick={handleSaveClick}
            className={styles.checkIcon}
          />
          <FontAwesomeIcon
            icon={faTimes}
            onClick={handleCancelClick}
            className={styles.cancelIcon}
          />
        </>
      )}
      {!editMode &&
        cierrStackingDate.getTime() > today.getTime() &&
        hasPermission && (
          <FontAwesomeIcon
            icon={faPen}
            onClick={() => {
              setEditMode(true);
            }}
            className={styles.editIcon}
          />
        )}
      {isSuccessColumn && !saving && (
        <>
          <div
            className={classNames(
              styles.successContainer,
              'isSuccessIconTarget'
            )}
            data-pr-tooltip={t('vgmUpdated')}
          >
            <FontAwesomeIcon
              icon={faCheckCircle}
              onClick={() => setEditMode(false)}
              className={classNames(styles.isSuccessIcon)}
            />
          </div>
          <Tooltip target=".isSuccessIconTarget" position="top" />
        </>
      )}
      {cierrStackingDate.getTime() < today.getTime() && (
        <>
          <div
            className={classNames(styles.infoContainer, 'infoIconTarget')}
            data-pr-tooltip={t('vgmPassedStacking')}
          >
            <FontAwesomeIcon
              icon={faInfoCircle}
              onClick={() => setEditMode(false)}
              className={classNames(styles.infoIcon)}
            />
          </div>
          <Tooltip target=".infoIconTarget" position="top" />
        </>
      )}

      {saving && <LoadingSpinner className={styles.loadingIcon} />}
    </div>
  );
};

export default EditVGM;
